import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { FuseCardComponent } from '@fuse/components/card';
import { Helpers } from 'app/core/helpers/helpers';
import { ToastService } from 'app/core/services/toast.service';
import { environment } from 'environments/environment';
import { AssociationService } from '../../associations/associations.service';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'app-map-dashboard',
  standalone: true,
  imports: [GoogleMapsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, ReactiveFormsModule, NgFor, NgIf, AsyncPipe, FuseCardComponent],
  templateUrl: './map-dashboard.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        width: 100%;
        min-height: 0;
      }
      :host ::ng-deep google-map,
      :host ::ng-deep google-map > div {
        width: 100% !important;
        height: 100% !important;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'w-full' },
})
export class MapDashboardComponent implements OnInit {
  private Association = inject(AssociationService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  profile: any;
  error: any;
  latitude: number;
  longitude: number;

  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  hoverMarker: any = null;

  pageLoader = false;
  protected map: any;
  zoom: number;
  center: google.maps.LatLngLiteral;
  searchData: any = {
    name: { column: 'name', operator: 'like', value: '', displayValue: '' },
  };
  markers: any[] = [];
  associationsList: any[] = [];
  allAssociationsList: any[] = [];
  selectedAssociation: any = {};
  where: any = [];
  count: any = ['unit'];

  searchControl = new FormControl('');
  filteredAssociations: any[] = [];

  mapOptions: google.maps.MapOptions = {
    maxZoom: 18,
    minZoom: 8,
    mapId: environment.googleMapId,
  };

  ngOnInit() {
    this.profile = this.auth.profile();
    this.latitude = !Helpers.empty(this.profile?.company?.latitude) ? parseFloat(this.profile.company.latitude) : 25.1274578;
    this.longitude = !Helpers.empty(this.profile?.company?.longitude) ? parseFloat(this.profile.company.longitude) : 55.2067104;
    this.zoom = 12;
    this.center = { lat: this.latitude, lng: this.longitude };
    this.getData();
  }

  public getData(isFiltered = false) {
    this.pageLoader = true;
    const where = [{ column: 'company_id', value: this.profile.company_id }, ...this.where];
    this.Association.getList({ where, count: this.count })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          this.markers = [];
          if (resp.status === 'success' && !Helpers.empty(resp.record)) {
            this.associationsList = resp.record;
            if (!isFiltered) {
              this.allAssociationsList = resp.record;
              this.filteredAssociations = resp.record;
            }
            resp.record.forEach((e: any) => {
              if (!Helpers.empty(e.latitude)) {
                const img = document.createElement('img');
                img.src = 'assets/icons/ISKAAN-Location-Marker.svg';
                img.style.width = '40px';
                img.style.height = '40px';
                this.markers = [...this.markers, { id: e.id, name: e.name, lat: e.latitude, lng: e.longitude, units: e.unit_count, type: e.association_type?.type, content: img }];
              }
            });
          }
          this.pageLoader = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.pageLoader = false;
          this.error = 'Error to get Communities';
          this.cdr.markForCheck();
        },
      });
  }

  selectedMarker(association_id: any) {
    if (this.profile.role.name === 'company_admin') {
      this.router.navigate(['/associations/details/' + association_id], { queryParams: { _ref: 'dashboard' } });
    } else {
      if (this.profile.my_associations.indexOf(association_id) != -1) {
        this.router.navigate(['/associations/details/' + association_id], { queryParams: { _ref: 'dashboard' } });
      } else {
        this.toast.error('You are not allowed to view the detail of this Communities.');
      }
    }
  }

  public autoFillSelected(event: any, field: string) {
    if (event) {
      this.searchData[field].value = event.name ? event[field] : '';
      this.where = Helpers.buildWhere(this.searchData);
      this.getData(true);
      const lat = parseFloat(event.latitude);
      const lng = parseFloat(event.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        this.center = { lat, lng };
        this.zoom = 13;
        this.cdr.markForCheck();
      }
    } else {
      this.searchData['name'].value = '';
      this.where = Helpers.buildWhere(this.searchData);
      this.filteredAssociations = [...this.allAssociationsList];
      this.getData();
      this.center = { lat: this.latitude, lng: this.longitude };
      this.zoom = 12;
      this.cdr.markForCheck();
    }
  }

  onSearchInput(event: any) {
    const val = (event.target as HTMLInputElement).value?.toLowerCase() || '';
    this.filteredAssociations = this.allAssociationsList.filter((a: any) => a.name?.toLowerCase().includes(val));
    if (!val) {
      this.searchData['name'].value = '';
      this.where = Helpers.buildWhere(this.searchData);
      this.getData();
    }
  }

  onAssociationSelected(association: any) {
    this.autoFillSelected(association, 'name');
    this.searchControl.setValue(association.name, { emitEvent: false });
  }

  getMarkerPosition(marker: any): google.maps.LatLngLiteral {
    return { lat: parseFloat(marker.lat), lng: parseFloat(marker.lng) };
  }
}
